/**
 * Guarded RPC client for Brunel Command (ADR-0005, ADR-0008).
 *
 * - Browsers may call only the owner-input RPCs; node RPCs are not exposed.
 * - Every mutation carries an idempotency UUID. A retry of the same logical
 *   mutation reuses its key, so the server returns the stored receipt.
 * - Tenant/session selectors travel as transport headers. They are untrusted
 *   hints; the server derives actor and authority itself.
 * - Success means "input received", never "privileged action executed".
 */
import type {
  GuardedRpcName,
  GuardedRpcRequest,
  GuardedRpcResult,
  RpcError,
  RpcResult,
} from '@brunel/shared';

export const BROWSER_RPC_NAMES = [
  'bootstrap_org',
  'post_owner_command',
  'post_chat_message',
  'set_codex_unrestricted',
  'manage_supabase_cli_credential',
  'set_ai_execution_profile',
  'start_worker_fresh_context',
] as const satisfies readonly GuardedRpcName[];

export type BrowserRpcName = (typeof BROWSER_RPC_NAMES)[number];

/** RPCs the database accepts only from an owner at assurance level aal2. */
export const STEP_UP_RPC_NAMES: readonly BrowserRpcName[] = [
  'bootstrap_org',
  'post_owner_command',
  'set_codex_unrestricted',
  'manage_supabase_cli_credential',
  'set_ai_execution_profile',
  'start_worker_fresh_context',
];

export interface RpcTransportContext {
  readonly orgSelector?: string;
  readonly sessionSelector?: string;
}

/** Minimal surface of a PostgREST RPC builder; lets tests avoid the network. */
export interface RpcResponse {
  data: unknown;
  error: { message: string } | null;
  /** HTTP status when a response arrived; absent/0 for network failures. */
  status?: number;
}

export interface RpcRequestBuilder {
  setHeader(name: string, value: string): RpcRequestBuilder;
  abortSignal(signal: AbortSignal): RpcRequestBuilder;
  then<T>(
    onfulfilled: (value: RpcResponse) => T,
    onrejected?: (reason: unknown) => T,
  ): PromiseLike<T>;
}

export interface RpcTransport {
  rpc(name: string, args: { request: unknown }): RpcRequestBuilder;
}

export interface PreparedMutation<N extends BrowserRpcName> {
  readonly name: N;
  readonly request: GuardedRpcRequest<N>;
  readonly context: RpcTransportContext;
  readonly idempotencyKey: string;
}

/** Upper bound for one attempt; a hung request must not block retries. */
export const REQUEST_TIMEOUT_MS = 15_000;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const ERROR_CODES = new Set(['unauthorized', 'forbidden', 'invalid_input', 'not_found', 'conflict', 'rate_limited', 'internal']);

export function isBrowserRpcName(name: string): name is BrowserRpcName {
  return (BROWSER_RPC_NAMES as readonly string[]).includes(name);
}

/** Fix the request and its idempotency key once; retries reuse the result. */
export function prepareMutation<N extends BrowserRpcName>(
  name: N,
  request: GuardedRpcRequest<N>,
  context: RpcTransportContext = {},
  newId: () => string = () => crypto.randomUUID(),
): PreparedMutation<N> {
  if (!isBrowserRpcName(name)) throw new Error('RPC is not available to browser clients');
  for (const selector of [context.orgSelector, context.sessionSelector]) {
    if (selector !== undefined && !UUID.test(selector)) throw new Error('Selectors must be UUIDs');
  }
  const idempotencyKey = newId();
  if (!UUID.test(idempotencyKey)) throw new Error('Idempotency key must be a UUID');
  // The payload is copied and frozen: a retry must resend exactly what the key was minted for.
  return Object.freeze({ name, request: deepFreeze(structuredClone(request)), context: Object.freeze({ ...context }), idempotencyKey });
}

function deepFreeze<T>(value: T): T {
  if (value && typeof value === 'object') {
    for (const item of Object.values(value)) deepFreeze(item);
    Object.freeze(value);
  }
  return value;
}

function asRpcError(value: unknown): RpcError {
  if (value && typeof value === 'object') {
    const candidate = value as { code?: unknown; message?: unknown };
    if (typeof candidate.code === 'string' && ERROR_CODES.has(candidate.code)) {
      return { code: candidate.code as RpcError['code'], message: typeof candidate.message === 'string' ? candidate.message : candidate.code };
    }
  }
  return { code: 'internal', message: 'unexpected response' };
}

/** Map an HTTP-level failure. Only network/5xx failures stay retryable ('internal'). */
function transportFailure(status: number | undefined): RpcError {
  if (status === 401) return { code: 'unauthorized', message: 'session expired' };
  if (status === 403) return { code: 'forbidden', message: 'forbidden' };
  if (status === 429) return { code: 'rate_limited', message: 'rate limited' };
  if (status !== undefined && status >= 400 && status < 500) return { code: 'invalid_input', message: 'request rejected' };
  return { code: 'internal', message: 'request failed' };
}

/** Normalize the SQL envelope; transport failures never look like success. */
export function parseRpcResponse<T>(data: unknown, transportError: { message: string } | null, status?: number): RpcResult<T> {
  if (transportError) return { ok: false, error: transportFailure(status) };
  if (!data || typeof data !== 'object') return { ok: false, error: { code: 'internal', message: 'empty response' } };
  const envelope = data as { ok?: unknown; value?: unknown; error?: unknown };
  if (envelope.ok === true && envelope.value && typeof envelope.value === 'object') {
    return { ok: true, value: envelope.value as T };
  }
  if (envelope.ok === false) return { ok: false, error: asRpcError(envelope.error) };
  return { ok: false, error: { code: 'internal', message: 'malformed response' } };
}

export async function sendMutation<N extends BrowserRpcName>(
  transport: RpcTransport,
  mutation: PreparedMutation<N>,
): Promise<RpcResult<GuardedRpcResult<N>>> {
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
  let builder = transport.rpc(mutation.name, { request: mutation.request })
    .abortSignal(timeout)
    .setHeader('Idempotency-Key', mutation.idempotencyKey);
  if (mutation.context.orgSelector) builder = builder.setHeader('X-Brunel-Org-Id', mutation.context.orgSelector);
  if (mutation.context.sessionSelector) builder = builder.setHeader('X-Brunel-Session-Id', mutation.context.sessionSelector);
  try {
    const { data, error, status } = await builder;
    return parseRpcResponse<GuardedRpcResult<N>>(data, error, status);
  } catch {
    return { ok: false, error: { code: 'internal', message: 'request failed' } };
  }
}

/**
 * Send with bounded retries for transport failures only. The same key is
 * reused, so a request that reached the server is never applied twice.
 */
export async function sendWithRetry<N extends BrowserRpcName>(
  transport: RpcTransport,
  mutation: PreparedMutation<N>,
  attempts = 3,
): Promise<RpcResult<GuardedRpcResult<N>>> {
  let last: RpcResult<GuardedRpcResult<N>> = { ok: false, error: { code: 'internal', message: 'not sent' } };
  for (let i = 0; i < Math.max(1, attempts); i += 1) {
    last = await sendMutation(transport, mutation);
    if (last.ok || last.error.code !== 'internal') return last;
  }
  return last;
}
